import { toast } from 'sonner'

import { clearQueryCache } from '@/lib/react-query'

import { HasRole } from '@/shared/auth/components/has-role'
import { ROLES } from '@/shared/auth/constants'
import { useTenantDetails } from '@/shared/auth/store'

import { Button } from '@/components/ui/button'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'

import { MenuLink, MobileMenuLink } from './link'

/**
 * Settings navigation (mobile scroller + desktop sidebar). Suspends on
 * `useTenantDetails`; gates the Authentication link behind the tenant's
 * SSO SKU and the whole Tenant-Settings group behind the Admin role.
 */
export const SettingsMenu = () => {
	const {
		data: { sku_info },
	} = useTenantDetails()

	// SSO is a SKU entitlement — only surface Authentication when the plan allows it.
	const hasSSOFlag = sku_info?.sso_allowed

	const handleClearCache = () => {
		clearQueryCache()
		toast.success('Cache cleared')
	}

	return (
		<>
			{/* Mobile: Horizontal scrollable navigation */}
			<div className="w-full lg:hidden">
				<ScrollArea className="w-full">
					<nav className="flex items-center gap-1.5 pb-3">
						{/* Personal */}
						<div className="flex shrink-0 items-center gap-1">
							<MobileMenuLink to="/settings/profile">Profile</MobileMenuLink>
							<MobileMenuLink to="/settings/notifications">
								Notifications
							</MobileMenuLink>
						</div>

						<HasRole role={ROLES.Admin}>
							<span className="text-border mx-1 h-4 w-px shrink-0 bg-current opacity-40" />
							<MobileMenuLink to="/settings/tenant-settings/members">
								Members
							</MobileMenuLink>
							<MobileMenuLink to="/settings/tenant-settings/invitations">
								Invitations
							</MobileMenuLink>
							{hasSSOFlag && (
								<MobileMenuLink to="/settings/tenant-settings/authentication">
									Authentication
								</MobileMenuLink>
							)}
							<MobileMenuLink to="/settings/tenant-settings/api-keys">
								API Keys
							</MobileMenuLink>
							<MobileMenuLink to="/settings/tenant-settings/notification-recipients">
								Notification Recipients
							</MobileMenuLink>
						</HasRole>

						<span className="text-border mx-1 h-4 w-px shrink-0 bg-current opacity-40" />
						<button
							type="button"
							onClick={handleClearCache}
							className="text-muted-foreground hover:bg-muted hover:text-foreground shrink-0 rounded-md px-3 py-1.5 text-sm transition-colors"
						>
							Clear Cache
						</button>
					</nav>
					<ScrollBar orientation="horizontal" />
				</ScrollArea>
			</div>

			{/* Desktop: Vertical sidebar */}
			<div className="hidden shrink-0 grow-0 flex-col items-start justify-start gap-8 lg:sticky lg:top-10 lg:flex lg:w-52">
				<div className="pb-4">
					<h4 className="mb-1 rounded-md px-2 py-1 text-sm font-semibold">
						Personal Settings
					</h4>

					<div className="flex flex-col items-start justify-start text-sm">
						<MenuLink to="/settings/profile">Profile</MenuLink>
						<MenuLink to="/settings/notifications">Notifications</MenuLink>
					</div>
				</div>

				<HasRole role={ROLES.Admin}>
					<div className="pb-4">
						<h4 className="mb-1 rounded-md px-2 py-1 text-sm font-semibold">
							Tenant Settings
						</h4>
						<div className="flex flex-col items-start justify-start text-sm">
							<MenuLink to="/settings/tenant-settings/members">
								Members
							</MenuLink>
							<MenuLink to="/settings/tenant-settings/invitations">
								Invitations
							</MenuLink>
							{hasSSOFlag && (
								<MenuLink to="/settings/tenant-settings/authentication">
									Authentication
								</MenuLink>
							)}
							<MenuLink to="/settings/tenant-settings/api-keys">
								API Keys
							</MenuLink>
							<MenuLink to="/settings/tenant-settings/notification-recipients">
								Notification Recipients
							</MenuLink>
						</div>
					</div>
				</HasRole>

				<div className="pb-4">
					<h4 className="mb-1 rounded-md px-2 py-1 text-sm font-semibold">
						Application
					</h4>

					<div className="flex flex-col items-start justify-start text-sm">
						<Button
							variant="link"
							className="text-muted-foreground flex items-center justify-start text-start"
							onClick={handleClearCache}
						>
							Clear Cache
						</Button>
					</div>
				</div>
			</div>
		</>
	)
}

/** Loading placeholder shown while SettingsMenu's tenant query suspends. */
export const SettingsMenuSkeleton = () => {
	return (
		<div className="flex flex-col items-start justify-start gap-8 lg:sticky lg:top-10 lg:flex lg:w-52">
			<div className="pb-4">
				<h4 className="mb-1 rounded-md px-2 py-1 text-sm font-semibold">
					Personal Settings
				</h4>

				<div className="flex flex-col items-start justify-start gap-3 text-sm">
					<Skeleton className="ml-2 h-4 w-24" />
					<Skeleton className="ml-2 h-4 w-24" />
				</div>
			</div>

			<div className="pb-4">
				<h4 className="mb-1 rounded-md px-2 py-1 text-sm font-semibold">
					Application
				</h4>

				<div className="flex flex-col items-start justify-start gap-3 text-sm">
					<Skeleton className="ml-2 h-4 w-24" />
				</div>
			</div>
		</div>
	)
}
